/**
 * `src/lib/ui/type/apply.ts` — the only place in the app that writes a
 * typographic custom property onto the document root.
 *
 * One function, because the reader's height cache depends on there being
 * exactly one moment at which the type scale changes. M2's virtualiser measures
 * 6,236 variable-height Arabic blocks; every one of those measurements is
 * invalid the instant `--type-scale` or `--leading-adjust` moves. A second
 * writer somewhere else would be an invalidation nobody subscribed to.
 */

import { typeVariables, type TypeScale } from './scale';

/** Anything with a `style` that accepts `setProperty` — `HTMLElement` in the
 *  app, a jsdom element in a test. Typed structurally so this module needs no
 *  DOM lib assumptions to be unit-tested. */
export interface StyleTarget {
	readonly style: { setProperty(property: string, value: string): void };
}

/**
 * Write the type scale onto `target` (the `<html>` element in the app).
 *
 * Returns the variables it wrote, so a caller that also needs to invalidate a
 * measurement cache can compare against what was there before without reading
 * back from the DOM.
 */
export function applyType(target: StyleTarget, scale: TypeScale): Record<string, string> {
	const variables = typeVariables(scale);
	for (const [property, value] of Object.entries(variables)) {
		target.style.setProperty(property, value);
	}
	return { ...variables };
}
